import { useForm } from '@tanstack/react-form'
import { createFileRoute, Link, Navigate } from '@tanstack/react-router'
import { useStore } from '@tanstack/react-store'
import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import {
  sessionHasDashboardRole,
  sessionIsGlobalAdmin,
  useAuth,
  useConfirmationLinkLogin,
  useLogin,
  useSupabaseSession,
} from '~/entities/session/model/use-auth'
import { AuthHeading, AuthShell, authLinkClassName } from '~/features/auth/auth-shell'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { createFieldValidators } from '~/shared/lib/form-utils'
import { storageManager } from '~/shared/lib/storage-manager'
import { loginSchema } from '~/shared/types'
import { Button } from '~/shared/ui/button'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { PasswordInput } from '~/shared/ui/password-input'

export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      email: (search.email as string) || '',
      reason: (search.reason as string) || '',
      intent: search.intent === 'upgrade' ? ('upgrade' as const) : '',
    }
  },
})

function LoginPage() {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth()
  const session = useSupabaseSession()
  const login = useLogin()
  const isConfirming = useConfirmationLinkLogin()
  const { email: searchEmail, reason, intent } = Route.useSearch()

  const toastShown = useRef(false)
  useEffect(() => {
    if (toastShown.current) return
    if (reason === 'forbidden') {
      toastShown.current = true
      toast.error(m.accessDeniedPleaseLogInAgain(), {
        duration: 4000,
      })
      window.history.replaceState({}, '', '/login')
    } else if (reason === 'expired') {
      toastShown.current = true
      toast.error(m.sessionExpiredPleaseLogInAgain(), {
        duration: 4000,
      })
      window.history.replaceState({}, '', '/login')
    }
  }, [reason])

  const form = useForm({
    defaultValues: {
      email: searchEmail || '',
      password: '',
    },
    onSubmit: ({ value }) => {
      login.mutate(value)
    },
  })

  const formValues = useStore(form.store, (state) => state.values)
  const emailValue = (formValues.email ?? '').trim()
  const passwordValue = (formValues.password ?? '').trim()
  const isSubmitDisabled = login.isPending || !emailValue || !passwordValue

  if (isAuthLoading || isConfirming) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="inline-block border-4 border-muted-foreground border-r-transparent border-solid rounded-full w-8 h-8 animate-spin motion-reduce:animate-[spin_1.5s_linear_infinite] align-[-0.125em]" />
        </div>
      </div>
    )
  }
  if (isAuthenticated) {
    if (intent === 'upgrade') {
      return <Navigate to="/upgrade" />
    }
    return <Navigate to="/dashboard" />
  }

  // A persisted admin session (already flagged as admin, e.g. after /admin-login)
  // belongs in the backoffice, not on the business login form or the upgrade flow.
  if (session && sessionIsGlobalAdmin(session) && storageManager.isAdmin()) {
    return <Navigate to="/admin" />
  }

  // A persisted personal session (valid, but no business role) belongs on the
  // upgrade flow, not the login form.
  if (session && !sessionHasDashboardRole(session)) {
    return <Navigate to="/upgrade" />
  }

  return (
    <AuthShell
      footer={
        <>
          <p>
            {m.dontHaveAnAccount()}{' '}
            <Link to="/register" className={authLinkClassName}>
              {m.signUpForFree()}
            </Link>
          </p>
          <p className="text-xs">{m.joinThousandsOfBusinessesGrowingWithPulsify()}</p>
          <Link
            to="/admin-login"
            search={{ reason: '' }}
            className="mt-8 text-muted-foreground/60 text-xs transition-colors hover:text-muted-foreground"
          >
            {m.adminLogin()}
          </Link>
        </>
      }
    >
      <AuthHeading
        title={m.welcomeBack()}
        description={m.readyToGrowYourBusinessLetsGetStarted()}
      />
      {intent === 'upgrade' && (
        <p className="squircle rounded-lg bg-muted p-3 text-foreground text-sm">
          {m.signInToUpgradeBanner()}
        </p>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        className="space-y-4"
      >
        <form.Field name="email" validators={createFieldValidators(loginSchema.shape.email)}>
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor={field.name}>{m.email()}</Label>
              <Input
                id={field.name}
                type="email"
                placeholder={m.businessexamplecom()}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
              />
              {field.state.meta.isTouched && field.state.meta.errors?.[0] && (
                <p className="text-destructive text-sm">
                  {typeof field.state.meta.errors[0] === 'string'
                    ? field.state.meta.errors[0]
                    : m.invalidValue()}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field name="password" validators={createFieldValidators(loginSchema.shape.password)}>
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor={field.name}>{m.password()}</Label>
              <PasswordInput
                id={field.name}
                placeholder={m.passwordPlaceholder()}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isSubmitDisabled) {
                    e.preventDefault()
                    form.handleSubmit()
                  }
                }}
              />
              {field.state.meta.isTouched && field.state.meta.errors?.[0] && (
                <p className="text-destructive text-sm">
                  {typeof field.state.meta.errors[0] === 'string'
                    ? field.state.meta.errors[0]
                    : m.invalidValue()}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <div className="flex justify-end">
          <Link
            to="/forgot-password"
            search={{ email: emailValue }}
            className="text-muted-foreground text-sm transition-colors hover:text-foreground"
          >
            {m.forgotPassword()}
          </Link>
        </div>

        {login.isError && (
          <div className="squircle rounded-lg border border-destructive/20 bg-destructive/10 p-3">
            <p className="text-destructive text-sm">{getErrorMessageFromError(login.error)}</p>
          </div>
        )}

        <Button
          type="submit"
          variant="brand"
          size="lg"
          className="w-full"
          disabled={isSubmitDisabled}
        >
          {login.isPending ? m.signingIn() : m.signIn()}
        </Button>
      </form>
    </AuthShell>
  )
}
